import { Dispatch } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store, createStore } from 'redux';
import { ActionType } from './ActionTypes';
import { EntryAction, entryReducer } from './entry/EntryActions';
import { EntryState, INITIAL_ENTRY_STATE } from './entry/EntryState';
import { PageStackAction, PageStackSelector, pageStackReducer } from './pageStack/PageStackActions';
import { INITIAL_PAGE_STACK_STATE, PageStackState } from './pageStack/PageStackState';
import { PapAction, papReducer } from './pap/PapActions';
import { INITIAL_PAP_STATE, PapState } from './pap/PapState';
import { PricingAction, PrivatePricingAction, pricingReducer } from './pricing/PricingActions';
import { INITIAL_PRICING_STATE, PricingState } from './pricing/PricingState';
import { PrivatePurchaseAction, PurchaseAction, purchaseReducer } from './purchase/PurchaseActions';
import { INITIAL_PURCHASE_STATE, PurchaseState } from './purchase/PurchaseState';
import { PrivateUs65PlusAction, Us65PlusAction, us65PlusReducer } from './us65Plus/Us65PlusActions';
import { INITIAL_US_65_PLUS_STATE, Us65PlusState } from './us65Plus/Us65PlusState';
import { PrivateUserAction, PublicUserAction, userReducer } from './user/UserActions';
import { UserSelector } from './user/UserSelector';
import { INITIAL_USER_STATE, UserState } from './user/UserState';
import { UserDataAction, UserDataSelector, userDataReducer } from './userData/UserDataActions';
import { INITIAL_USER_DATA_STATE, UserDataState } from './userData/UserDataState';

export type PublicStoreAction =
  | PageStackAction
  | UserDataAction
  | PublicUserAction
  | EntryAction
  | PricingAction
  | PurchaseAction
  | PapAction
  | Us65PlusAction;

export type PrivateStoreAction =
  | PrivateUserAction
  | PrivatePricingAction
  | PrivatePurchaseAction
  | PrivateUs65PlusAction;

export type StoreAction = PublicStoreAction | PrivateStoreAction;
export type StoreType = Store<StoreStateInternal, StoreAction>;
export type AppStore = Store<StoreStateInternal, StoreAction>;

export type StoreStateInternal = {
  pageStack: PageStackState;
  user: UserState;
  userData: UserDataState;
  entry: EntryState;
  pricing: PricingState;
  purchase: PurchaseState;
  pap: PapState;
  us65Plus: Us65PlusState;
};

export const INITIAL_STATE: StoreStateInternal = {
  pageStack: INITIAL_PAGE_STACK_STATE,
  user: INITIAL_USER_STATE,
  userData: INITIAL_USER_DATA_STATE,
  entry: INITIAL_ENTRY_STATE,
  pricing: INITIAL_PRICING_STATE,
  purchase: INITIAL_PURCHASE_STATE,
  pap: INITIAL_PAP_STATE,
  us65Plus: INITIAL_US_65_PLUS_STATE,
};

export function reducer(
  state: StoreStateInternal = INITIAL_STATE,
  action: StoreAction
): StoreStateInternal {
  const nextState: StoreStateInternal = {
    pageStack: pageStackReducer(state.pageStack, action),
    entry: entryReducer(state.entry, action),
    user: userReducer(state.user, action),
    userData: userDataReducer(state.userData, action),
    pricing: pricingReducer(state.pricing, action),
    purchase: purchaseReducer(state.purchase, action),
    pap: papReducer(state.pap, action),
    us65Plus: us65PlusReducer(state.us65Plus, action),
  };
  if (action.type === ActionType.SET_ENTRY) {
    const couponCode = couponCodeOnEntry(state, action.entry);
    return { ...nextState, pricing: { ...nextState.pricing, couponCode } };
  }
  return nextState;
}

/**
 * The coupon once Entry lands. A PAP or partner-insurer coupon wins, as it was earned through a
 * completed flow. Otherwise the URL coupon wins over one already set.
 */
function couponCodeOnEntry(state: StoreStateInternal, entry: EntryState): string | undefined {
  return state.pap.scoreResult !== undefined || state.userData.usedPartnerInsurance?.[0] === 'Yes'
    ? (state.pricing.couponCode ?? entry.couponCode)
    : (entry.couponCode ?? state.pricing.couponCode);
}

/**
 * React hook for FunctionComponents to retrieve state from store. Stronger typing than useSelector.
 * `useAppStore` name chosen to avoid conflict with redux's `useStore` hook
 */
export function useAppStore<Selected>(
  selector: (state: StoreStateInternal) => Selected,
  equalityFn?: (left: Selected, right: Selected) => boolean
): Selected {
  return useSelector<StoreStateInternal, Selected>(selector, equalityFn);
}

/**
 * React hook for FunctionComponents to dispatch app actions to the store. Stronger typing than useDispatch.
 */
export function useAppDispatch(): Dispatch<PublicStoreAction> {
  return useDispatch<Dispatch<PublicStoreAction>>();
}

export const AppSelector = {
  ...PageStackSelector,
  ...UserSelector,
  ...UserDataSelector,
};

export const store = createStore(reducer, window.__REDUX_DEVTOOLS_EXTENSION__?.());
